import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface SatisfactionStatsData {
  stats: {
    totalResponses: number;
    totalRatings: number;
    noResponse: number;
    satisfaction: {
      yes: number;
      no: number;
      invalid: number;
      yesPercent: number;
      noPercent: number;
      invalidPercent: number;
    };
    rating: {
      bad: number;
      medium: number;
      good: number;
      invalid: number;
      badPercent: number;
      mediumPercent: number;
      goodPercent: number;
      invalidPercent: number;
    };
  };
  recentResponses: Array<{
    id: string;
    protocolId: string;
    customerName: string;
    customerPhone: string;
    satisfactionResponse: string | null;
    satisfactionResponseValid: boolean;
    rating: string | null;
    ratingValid: boolean;
    createdAt: Date;
    department?: {
      id: string;
      name: string;
    } | null;
  }>;
  availableDepartments?: Array<{
    id: string;
    name: string;
  }>;
}

export default function SatisfactionStats() {
  const { agent, isAdmin, isDepartmentManager } = useAuth();
  const [data, setData] = useState<SatisfactionStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadStats = async () => {
    try {
      setLoading(true);
      const result = await api.getSatisfactionStats(startDate || undefined, endDate || undefined);

      // Debug: Log received data
      console.log('[SatisfactionStats] Received data from API:', result);
      console.log('[SatisfactionStats] Stats breakdown:', {
        totalSurveysSent: result.stats.totalResponses + result.stats.noResponse,
        totalResponses: result.stats.totalResponses,
        totalRatings: result.stats.totalRatings,
        noResponse: result.stats.noResponse,
        satisfaction: result.stats.satisfaction,
        rating: result.stats.rating,
        recentResponsesCount: result.recentResponses.length
      });

      setData(result);
    } catch (error) {
      console.error('Error loading satisfaction stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleFilter = () => {
    setCurrentPage(1);
    loadStats();
  };

  const clearFilter = () => {
    setStartDate('');
    setEndDate('');
    setDepartmentFilter('');
    setCurrentPage(1);
    setTimeout(() => loadStats(), 0);
  };

  if (!agent || (!isAdmin() && !isDepartmentManager() && agent.role !== 'GENERAL_MANAGER')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Acesso Negado</h2>
          <p className="text-gray-600 dark:text-gray-400">Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  // Filter responses by department
  const filteredResponses = data?.recentResponses.filter(response => {
    if (!departmentFilter) return true;
    if (departmentFilter === 'none') return !response.department;
    return response.department?.id === departmentFilter;
  }) || [];

  // Recalculate statistics based on filtered responses
  const filteredStats = filteredResponses.length > 0 ? (() => {
    const totalResponses = filteredResponses.filter(r => r.satisfactionResponse).length;
    const totalRatings = filteredResponses.filter(r => r.rating).length;
    const noResponse = filteredResponses.filter(r => !r.satisfactionResponse && !r.rating).length;

    const satisfactionYes = filteredResponses.filter(r => r.satisfactionResponse === 'SIM').length;
    const satisfactionNo = filteredResponses.filter(r => r.satisfactionResponse === 'NAO').length;
    const satisfactionInvalid = filteredResponses.filter(r => r.satisfactionResponse && r.satisfactionResponse !== 'SIM' && r.satisfactionResponse !== 'NAO').length;

    const ratingBad = filteredResponses.filter(r => r.rating === 'RUIM').length;
    const ratingMedium = filteredResponses.filter(r => r.rating === 'MEDIO').length;
    const ratingGood = filteredResponses.filter(r => r.rating === 'BOM').length;
    const ratingInvalid = filteredResponses.filter(r => r.rating && r.rating !== 'RUIM' && r.rating !== 'MEDIO' && r.rating !== 'BOM').length;

    return {
      totalResponses,
      totalRatings,
      noResponse,
      satisfaction: {
        yes: satisfactionYes,
        no: satisfactionNo,
        invalid: satisfactionInvalid,
        yesPercent: totalResponses > 0 ? Math.round((satisfactionYes / totalResponses) * 1000) / 10 : 0,
        noPercent: totalResponses > 0 ? Math.round((satisfactionNo / totalResponses) * 1000) / 10 : 0,
        invalidPercent: totalResponses > 0 ? Math.round((satisfactionInvalid / totalResponses) * 1000) / 10 : 0,
      },
      rating: {
        bad: ratingBad,
        medium: ratingMedium,
        good: ratingGood,
        invalid: ratingInvalid,
        badPercent: totalRatings > 0 ? Math.round((ratingBad / totalRatings) * 1000) / 10 : 0,
        mediumPercent: totalRatings > 0 ? Math.round((ratingMedium / totalRatings) * 1000) / 10 : 0,
        goodPercent: totalRatings > 0 ? Math.round((ratingGood / totalRatings) * 1000) / 10 : 0,
        invalidPercent: totalRatings > 0 ? Math.round((ratingInvalid / totalRatings) * 1000) / 10 : 0,
      },
    };
  })() : data?.stats;

  const displayStats = departmentFilter ? filteredStats : data?.stats;

  // Pagination logic for filtered responses
  const totalResponsesCount = filteredResponses.length;
  const totalPages = Math.ceil(totalResponsesCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResponses = filteredResponses.slice(startIndex, endIndex);

  return (
    <div className="h-full overflow-auto bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Estatísticas de Satisfação
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Acompanhe os resultados das pesquisas de satisfação
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Inicial
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Final
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            {(isAdmin() || agent?.role === 'GENERAL_MANAGER') && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Departamento
                </label>
                <select
                  value={departmentFilter}
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos os departamentos</option>
                  {data?.availableDepartments?.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                  <option value="none">Sem departamento</option>
                </select>
              </div>
            )}
            <button
              onClick={handleFilter}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Filtrar
            </button>
            {(startDate || endDate) && (
              <button
                onClick={clearFilter}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Limpar
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6 mb-8">
              {/* Total Surveys Started */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Pesquisas Enviadas
                  </h3>
                  <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {displayStats ? displayStats.totalResponses + displayStats.noResponse : 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Total iniciadas
                </p>
              </div>

              {/* Satisfaction Responses */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    1ª Pergunta
                  </h3>
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {displayStats?.totalResponses || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Respondidas
                </p>
              </div>

              {/* Rating Responses */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    2ª Pergunta
                  </h3>
                  <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {displayStats?.totalRatings || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Avaliações completas
                </p>
              </div>

              {/* Satisfied */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Satisfeitos
                  </h3>
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {displayStats && displayStats.totalResponses > 0 ? displayStats.satisfaction.yesPercent : 0}%
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {displayStats?.satisfaction.yes || 0} de {displayStats?.totalResponses || 0}
                </p>
              </div>

              {/* Unsatisfied */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Insatisfeitos
                  </h3>
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {displayStats && displayStats.totalResponses > 0 ? displayStats.satisfaction.noPercent : 0}%
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {displayStats?.satisfaction.no || 0} de {displayStats?.totalResponses || 0}
                </p>
              </div>

              {/* No Response */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Sem Resposta
                  </h3>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-500 dark:text-gray-400">
                  {displayStats?.noResponse || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Fechadas automaticamente
                </p>
              </div>

              {/* Invalid Responses */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Resposta Inválida
                  </h3>
                  <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-orange-500 dark:text-orange-400">
                  {displayStats?.satisfaction.invalid || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Respostas não reconhecidas
                </p>
              </div>
            </div>

            {/* No Data Info */}
            {displayStats && displayStats.totalResponses === 0 && displayStats.totalRatings === 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Nenhuma resposta ainda
                    </h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      {displayStats && displayStats.noResponse > 0
                        ? `Existem ${displayStats.noResponse} pesquisa(s) enviada(s), mas nenhum cliente respondeu ainda.`
                        : 'Não há pesquisas enviadas neste período.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Rating Breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Distribuição de Avaliações
              </h2>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Excelente</span>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400">
                      {displayStats?.rating.goodPercent || 0}% ({displayStats?.rating.good || 0})
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${displayStats?.rating.goodPercent || 0}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Bom</span>
                    <span className="text-sm font-bold text-yellow-600 dark:text-yellow-400">
                      {displayStats?.rating.mediumPercent || 0}% ({displayStats?.rating.medium || 0})
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-yellow-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${displayStats?.rating.mediumPercent || 0}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ruim</span>
                    <span className="text-sm font-bold text-red-600 dark:text-red-400">
                      {displayStats?.rating.badPercent || 0}% ({displayStats?.rating.bad || 0})
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-red-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${displayStats?.rating.badPercent || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Responses */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Respostas Recentes
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Protocolo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Telefone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Departamento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Satisfeito
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Avaliação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Data
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedResponses.map((response) => (
                      <tr key={response.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {response.protocolId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {response.customerName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {response.customerPhone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {response.department?.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {response.satisfactionResponse === 'SIM' ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                Sim
                              </span>
                              {!response.satisfactionResponseValid && (
                                <span className="text-xs text-gray-400" title="Resposta por texto, não por botão">
                                  *
                                </span>
                              )}
                            </div>
                          ) : response.satisfactionResponse === 'NAO' ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                Não
                              </span>
                              {!response.satisfactionResponseValid && (
                                <span className="text-xs text-gray-400" title="Resposta por texto, não por botão">
                                  *
                                </span>
                              )}
                            </div>
                          ) : response.satisfactionResponse ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {response.satisfactionResponse}
                              </span>
                              <span className="text-xs text-gray-400" title="Resposta inválida">
                                *
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {response.rating === 'BOM' ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                Excelente
                              </span>
                              {!response.ratingValid && (
                                <span className="text-xs text-gray-400" title="Resposta por texto, não por botão">
                                  *
                                </span>
                              )}
                            </div>
                          ) : response.rating === 'MEDIO' ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                Bom
                              </span>
                              {!response.ratingValid && (
                                <span className="text-xs text-gray-400" title="Resposta por texto, não por botão">
                                  *
                                </span>
                              )}
                            </div>
                          ) : response.rating === 'RUIM' ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                Ruim
                              </span>
                              {!response.ratingValid && (
                                <span className="text-xs text-gray-400" title="Resposta por texto, não por botão">
                                  *
                                </span>
                              )}
                            </div>
                          ) : response.rating ? (
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {response.rating}
                              </span>
                              <span className="text-xs text-gray-400" title="Resposta inválida">
                                *
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {new Date(response.createdAt).toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.recentResponses.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhuma resposta encontrada no período selecionado.
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {totalResponsesCount > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(endIndex, totalResponsesCount)}</span> de{' '}
                    <span className="font-medium">{totalResponsesCount}</span> respostas
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      translate="no"
                    >
                      Anterior
                    </button>
                    <span className="text-sm text-gray-700 dark:text-gray-300" translate="no">
                      Página <span className="font-medium">{currentPage}</span> de{' '}
                      <span className="font-medium">{totalPages}</span>
                    </span>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      translate="no"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              Erro ao carregar estatísticas. Tente novamente.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
